import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Mail } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import rateLimitService from '@/services/RateLimitService';

interface ReferralEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  provider: {
    id: string;
    facility_name: string;
    contact_email: string;
    contact_person: string;
  };
}

const REFERRAL_CONTEXTS = [
  'Housing / facing homelessness',
  'Mental health support',
  'Assistance with ADLs (Activities of Daily Living)',
  'Medication management',
  'Memory care / dementia support',
  'Behavioral support needs',
  'Substance use recovery support',
  'Respite care', 
  'Transportation assistance',
  'Social / community engagement',
  'Financial management or benefits support'
];

const ReferralEmailModal = ({ isOpen, onClose, provider }: ReferralEmailModalProps) => {
  const { user } = useAuth();
  const [selectedContexts, setSelectedContexts] = useState<string[]>([]);
  const [otherContext, setOtherContext] = useState('');
  const [phiConfirmed, setPhiConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleContextToggle = (context: string) => {
    setSelectedContexts(prev => 
      prev.includes(context)
        ? prev.filter(c => c !== context)
        : [...prev, context]
    );
  };

  const generateEmailContent = () => {
    const contexts = [...selectedContexts];
    if (otherContext.trim()) {
      contexts.push(`Other: ${otherContext.trim()}`);
    }

    const subject = `Referral Inquiry - ${provider.facility_name}`;
    const body = `Dear ${provider.contact_person},\n\nI am reaching out regarding a potential placement referral for your facility.\n\nClient Support Areas Needed:\n${contexts.map(context => `• ${context}`).join('\n')}\n\nPlease let me know if you have availability and if these support needs align with your services.\n\nThank you for your time.\n\nBest regards,\n[Your name and organization]\n\n---\nThis email was initiated through CareKonnect. Please reply to this email to continue the conversation.`;

    return { subject, body };
  };

  const handleContinueToEmail = async () => {
    if (!phiConfirmed) {
      toast.error('Please confirm you will not include PHI/PII');
      return;
    }

    if (selectedContexts.length === 0 && !otherContext.trim()) {
      toast.error('Please select at least one support area');
      return;
    }

    // Check rate limit
    const rateCheck = rateLimitService.checkRateLimit(user?.id || 'anonymous', 'email');
    if (!rateCheck.allowed) {
      const remainingTime = Math.ceil(rateLimitService.getRemainingTime(user?.id || 'anonymous', 'email') / 60000);
      toast.error(`Rate limit exceeded. Please wait ${remainingTime} minute(s) before sending another referral email.`);
      return;
    }

    setLoading(true);

    try {
      // Create referral record with metadata only
      const contexts = [...selectedContexts];
      if (otherContext.trim()) {
        contexts.push(`Other: ${otherContext.trim()}`);
      }

      const { error } = await supabase
        .from('referrals')
        .insert({
          referring_user: user?.id,
          linked_listing: provider.id,
          provider_email: provider.contact_email,
          provider_name: provider.facility_name,
          context_tags: contexts,
          status: 'submitted',
          county: 'Unknown', // Required field, will be updated if needed
          date_initiated: new Date().toISOString()
        });

      if (error) throw error;

      // Generate email content and open email client
      const { subject, body } = generateEmailContent();
      const mailtoLink = `mailto:${provider.contact_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      
      window.location.href = mailtoLink;
      
      toast.success('Referral initiated! Email client opened.');
      toast.info(`${rateCheck.remaining} email referrals remaining this hour.`);
      onClose();
    } catch (error) {
      console.error('Error creating referral:', error);
      toast.error('Failed to initiate referral');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedContexts([]);
    setOtherContext('');
    setPhiConfirmed(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Submit Referral</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* PHI/PII Warning */}
          <Alert className="border-warning bg-warning/10">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <AlertDescription className="text-warning-foreground text-sm">
              Placement professionals must not include PHI/PII in referral-related emails sent via CareKonnect.
            </AlertDescription>
          </Alert>

          {/* Instructions */}
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Select the support areas your client needs. You may choose more than one. 
              Do not enter names, DOBs, or other identifiers.
            </p>
          </div>

          {/* Provider Info */}
          <div className="p-3 sm:p-4 bg-secondary/20 rounded-lg">
            <h3 className="font-medium">{provider.facility_name}</h3>
            <p className="text-sm text-muted-foreground">Contact: {provider.contact_person}</p>
            <p className="text-sm text-muted-foreground break-all">{provider.contact_email}</p>
          </div>

          {/* Referral Context Checklist */}
          <div className="space-y-3">
            <h3 className="font-medium">Referral Context (select all that apply):</h3>
            <div className="grid grid-cols-1 gap-3 max-h-48 overflow-y-auto">
              {REFERRAL_CONTEXTS.map((context) => (
                <div key={context} className="flex items-center space-x-2">
                  <Checkbox
                    id={context}
                    checked={selectedContexts.includes(context)}
                    onCheckedChange={() => handleContextToggle(context)}
                  />
                  <label
                    htmlFor={context}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {context}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Other Context */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Other (non-PHI):</label>
            <Textarea
              placeholder="Use general descriptors only..."
              value={otherContext}
              onChange={(e) => setOtherContext(e.target.value)}
              maxLength={140}
              className="resize-none text-sm"
              rows={3}
            />
            <div className="flex justify-between items-center text-xs">
              <span className="text-destructive font-medium">
                Do not include PHI/PII. Use general descriptors only.
              </span>
              <span className="text-muted-foreground">
                {otherContext.length}/140
              </span>
            </div>
          </div>

          {/* Selected Contexts Preview */}
          {(selectedContexts.length > 0 || otherContext.trim()) && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Selected Support Areas:</h4>
              <div className="flex flex-wrap gap-2">
                {selectedContexts.map((context) => (
                  <Badge key={context} variant="secondary" className="text-xs">
                    {context}
                  </Badge>
                ))}
                {otherContext.trim() && (
                  <Badge variant="secondary" className="text-xs">
                    Other: {otherContext.trim()}
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* PHI Confirmation */}
          <div className="flex items-start space-x-2">
            <Checkbox
              id="phi-confirmation"
              checked={phiConfirmed}
              onCheckedChange={(checked) => setPhiConfirmed(checked === true)}
              className="mt-1"
            />
            <label
              htmlFor="phi-confirmation"
              className="text-sm font-medium leading-relaxed cursor-pointer"
            >
              I confirm I will not include PHI/PII in the referral email.
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              onClick={handleContinueToEmail}
              disabled={!phiConfirmed || loading || (selectedContexts.length === 0 && !otherContext.trim())}
              className="flex-1 w-full sm:w-auto"
            >
              <Mail className="h-4 w-4 mr-2" />
              {loading ? 'Creating...' : 'Continue to Email'}
            </Button>
            <Button variant="outline" onClick={handleClose} disabled={loading} className="w-full sm:w-auto">
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReferralEmailModal;
